import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useServices } from '../../app/services';
import { spendingSign, type BudgetMember, type Category, type Subcategory, type Transaction } from '../../domain/model';
import { formatDayMonth } from '../../i18n/format';
import { Amount, CategoryDot, ListRow, series, useFormat } from '../../ui';
import { categoryLabel } from '../categories/labels';
import { useMemberName } from './people';

/** What a list needs to name an entry's category and people. */
export interface EntryLabels {
  categories: ReadonlyMap<string, Category>;
  subcategories: ReadonlyMap<string, Subcategory>;
  /** Everyone who was ever in the entry's budget (a payer who left keeps a name). */
  members: ReadonlyMap<string, BudgetMember>;
  /** Budgets with more than one person now: their entries say who paid and whose. */
  sharedSpaces: ReadonlySet<string>;
}

export function entryLabel(t: TFunction, e: Transaction, labels: Pick<EntryLabels, 'categories' | 'subcategories'>): string {
  return categoryLabel(t, (e.subcategoryId && labels.subcategories.get(e.subcategoryId)) || labels.categories.get(e.categoryId));
}

/** Entries that are not plain spending say so: refunds subtract, transfers and tracked card bills do not count. */
export function entryTag(t: TFunction, e: Transaction): string | null {
  if (e.type === 'refund') return t('home.tag.refund');
  if (e.type === 'transfer') return t('home.tag.transfer');
  if (e.type === 'credit_card_payment' && !e.countsAsSpending) return t('home.tag.cardTracked');
  return null;
}

/**
 * One entry in a list: category, what makes it special, who paid and whose
 * (in a shared budget), date and amount. Tapping opens it.
 */
export function EntryRow({ entry: e, labels, extra }: { entry: Transaction; labels: EntryLabels; extra?: string | null }) {
  const { t } = useTranslation();
  const { language, identity } = useServices();
  const navigate = useNavigate();
  const location = useLocation();
  const name = useMemberName();
  const { money } = useFormat();
  const people: string[] = [];
  if (labels.sharedSpaces.has(e.spaceId)) {
    const payer = labels.members.get(e.paidBy);
    if (payer && payer.userId !== identity.session.userId) people.push(t('entry.paidTag', { name: name(payer) }));
    if (e.ownership === 'personal' && e.ownerMemberId) people.push(t('entry.forTag', { name: name(labels.members.get(e.ownerMemberId)) }));
  }
  const sign = spendingSign(e);
  return (
    <ListRow
      density="compact"
      leading={<CategoryDot color={series(labels.categories.get(e.categoryId)?.colorSlot ?? 0)} />}
      title={entryLabel(t, e, labels)}
      subtitle={
        [
          entryTag(t, e),
          e.originalCurrency && e.originalAmountMinor ? t('paidIn.paidTag', { amount: money(e.originalAmountMinor, { currency: e.originalCurrency }) }) : null,
          e.provider,
          ...people,
          extra,
        ]
          .filter(Boolean)
          .join(' · ') || undefined
      }
      meta={formatDayMonth(e.date, language)}
      trailing={<Amount minor={sign * e.amountMinor || e.amountMinor} currency={e.currency} signed={sign === -1} tone={sign === 0 ? 'muted' : 'default'} />}
      onClick={() => navigate(`/entry/${e.id}`, { state: { background: location } })}
      chevron={false}
    />
  );
}
